import React from 'react'

import { Tooltip } from '@mantine/core'

import { CustomFieldStatus } from 'src/graphql/types/customFields'
import { formatValueForDisplay } from 'src/pages/ApplicantDetailsPage/sidebar/components/customFieldsUtils'

// Free-text custom fields can hold arbitrarily long values. Cap the displayed
// text at a reasonable character count so a single long entry can't blow out
// the column width — the full value is always available on hover.
const MAX_DISPLAY_CHARS = 40

/**
 * Generates dynamic table column definitions from custom fields
 * Includes all active custom fields (regardless of whether they're
 * searchable/filterable) so any field type — including free-text — can be
 * displayed as a column. All columns are hidden by default.
 */
export const generateCustomFieldColumns = (customFields) => {
  if (!customFields || customFields.length === 0) {
    return []
  }

  // All active custom fields are displayable as columns
  const displayableFields = customFields
    .filter((f) => f.status === CustomFieldStatus.ACTIVE)
    .sort((a, b) => a.sortOrder - b.sortOrder)

  return displayableFields.map((field) => ({
    key: `customField_${field.customFieldId}`,
    label: field.label,
    hidden: true, // Hidden by default
    render: (row) => {
      const value = row.customFieldValues?.find(
        (v) => v.customFieldId === field.customFieldId
      )
      const displayValue = value ? formatValueForDisplay(field, value) : '—'
      // Cap long values at a fixed character count and reveal the full value on
      // hover. The `truncate` class still ellipsis-clips within narrow columns.
      const isTruncated = displayValue.length > MAX_DISPLAY_CHARS
      const truncatedValue = isTruncated
        ? `${displayValue.slice(0, MAX_DISPLAY_CHARS).trimEnd()}…`
        : displayValue
      return (
        <Tooltip
          label={displayValue}
          withArrow
          multiline
          maw={320}
          disabled={displayValue === '—' || !isTruncated}
        >
          <div className="truncate text-xs font-medium text-doubleNickel-gray-900">
            {truncatedValue}
          </div>
        </Tooltip>
      )
    },
  }))
}

/**
 * Check if a column key represents a custom field
 */
export const isCustomFieldColumn = (key: string): boolean => {
  return key.startsWith('customField_')
}

/**
 * Extract custom field ID from column key
 */
export const getCustomFieldIdFromKey = (key: string): string => {
  return key.replace('customField_', '')
}

/**
 * Generate column key from custom field ID
 */
export const getCustomFieldKeyFromId = (id: string): string => {
  return `customField_${id}`
}

/**
 * Generate default visibility settings for custom field columns
 * All active custom field columns are hidden by default
 */
export const generateCustomFieldVisibility = (
  customFields
): Record<string, boolean> => {
  if (!customFields || customFields.length === 0) {
    return {}
  }

  const visibility = {}
  customFields
    .filter((f) => f.status === CustomFieldStatus.ACTIVE)
    .forEach((field) => {
      visibility[`customField_${field.customFieldId}`] = false
    })

  return visibility
}

/**
 * Generate default order for custom field columns
 * Returns array of column keys in sortOrder
 */
export const generateCustomFieldOrder = (customFields): string[] => {
  if (!customFields || customFields.length === 0) {
    return []
  }

  return customFields
    .filter((f) => f.status === CustomFieldStatus.ACTIVE)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((field) => `customField_${field.customFieldId}`)
}
